/* eslint-disable max-classes-per-file */
import Dexie from 'dexie';
import dayjs from 'lib/dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import AddFields from 'helpers/nested-fields/addFields';
import isEqual from 'lodash.isequal';
import { nextFrame } from 'helpers/timing';
import { applyEncryptionMiddleware } from 'lib/dexie';
import { metaValue } from 'helpers/dom-helper';
import ApplicationController from './application_controller';

dayjs.extend(relativeTime);

export default class extends ApplicationController {
  initialize() {
    this.onSubmit = this.onSubmit.bind(this);
    this.onSuccessfulSubmission = this.onSuccessfulSubmission.bind(this);
    this.onUnSuccessfulSubmission = this.onUnSuccessfulSubmission.bind(this);
    this.loadHistory = this.loadHistory.bind(this);
    this.refreshHistory = this.refreshHistory.bind(this);
    this.save = this.save.bind(this);

    this.db = new Dexie('FormStore');
    this.db.version(2).stores({
      forms: `
        ++id,
        form_id,
        time,
        submitted,
        [form_id+submitted]
      `,
    });

    applyEncryptionMiddleware(this.db);
  }

  async connect() {
    if (!this.shouldAutoSave) return;
    if (!this.element.id) throw new Error('<form> element should have a unique `id`');

    await this.db.open();
    this.formHistory = new FormHistory(this.db, { form: this.element, historyLimit: this.autoSaveHistoryLength });
    // Native form submits bypass `ajax:success`, so post-submit entries get
    // marked `submitted: 'true'` but never deleted. Prune them on connect so
    // they don't accumulate or leak into the history modal.
    await this.formHistory.pruneSubmitted();

    this.element.addEventListener('submit', this.onSubmit);
    this.element.addEventListener('ajax:success', this.onSuccessfulSubmission);
    this.element.addEventListener('ajax:error', this.onUnSuccessfulSubmission);
    document.addEventListener('auto-save--history:load', this.loadHistory);
    document.addEventListener('auto-save--history:refresh', this.refreshHistory);
    document.addEventListener('session-timeout', this.save);

    this.interval = setInterval(this.autoSave.bind(this), this.autoSaveInterval * 1000);
  }

  disconnect() {
    this.formHistory?.destroy();

    this.element.removeEventListener('submit', this.onSubmit);
    this.element.removeEventListener('ajax:success', this.onSuccessfulSubmission);
    this.element.removeEventListener('ajax:error', this.onUnSuccessfulSubmission);
    document.removeEventListener('auto-save--history:load', this.loadHistory);
    document.removeEventListener('auto-save--history:refresh', this.refreshHistory);
    document.removeEventListener('session-timeout', this.save);

    if (this.interval) clearInterval(this.interval);
  }

  /**
   * We only want to handle `onSubmit` when the form does not intercept Turbo's event
   */
  async onSubmit(event) {
    // A capture-phase guard (e.g. required-fields) may have refused this submit. Honor the
    // refusal — resubmitting natively below would bypass it entirely.
    if (event.defaultPrevented) return;
    if (this.isRemote || !event.submitter) return;

    event.preventDefault();
    this.formHistory?.save(false);

    if (!this.isOnline) {
      $('#offline-form-saved').toast('show');
      // Reenable submit button. Need to wait for sometime to avoid race conditions
      const submitBtn = this.element.querySelector('input[type="submit"]');
      if (submitBtn) setTimeout(() => submitBtn.removeAttribute('disabled'), 100);
      return;
    }

    await this.formHistory?.histories.modify({ submitted: 'true' });
    this.element.submit();
  }

  /**
   * If the form submission is successful, let's delete all the form related data. This is not true for `onSubmit`
   * handler because it does not use Turbo's event, and hence it is not possible to know if we really did write on
   * the database. In that case, we mark `submitted` as `true` locally.
   * We recommend using Turbo with `form_for` or `form_with` unless there is a solid reason not to.
   */
  async onSuccessfulSubmission() {
    await this.formHistory?.histories.delete();
  }

  async onUnSuccessfulSubmission() {
    this.formHistory?.save(false); // Store a fresh copy of the form fields
    if (!this.isOnline) $('#offline-form-saved').toast('show');
  }

  async loadHistory(event) {
    const { id } = event.detail;
    if (!id) return;

    await this.formHistory?.load(id);
    this.dispatch('loaded');
  }

  async refreshHistory() {
    await this.formHistory?.populateHistories();
    this.dispatch('history-in-sync', { bubbles: true });
  }

  async autoSave() {
    const saved = await this.save();
    if (saved) {
      await this.checkIfHistoryOutOfSync();
    }
  }

  async save(showToast = true) {
    if (!this.shouldAutoSave) return false;
    const isDirty = await this.isFormDirty();
    if (!isDirty) return false;
    await this.formHistory?.save(showToast);
    return true;
  }

  // Private

  async isFormDirty() {
    // Check if form fields have changed
    const lastHistory = await this.formHistory?.histories.last();
    return !isEqual(lastHistory?.children, traverseDOM(this.element));
  }

  get shouldAutoSave() {
    return !!metaValue('auto-save');
  }

  get autoSaveInterval() {
    return this.parseIntFromMeta('auto-save-interval', 30);
  }

  get autoSaveHistoryLength() {
    return this.parseIntFromMeta('auto-save-history-length', 10);
  }

  parseIntFromMeta(name, defaultValue) {
    const value = Number.parseInt(metaValue(name), 10);
    return Number.isNaN(value) ? defaultValue : value;
  }

  get isRemote() {
    return this.element.hasAttribute('data-remote');
  }

  // If we save the last history when the history modal is still open, we need to let the users know that the history
  // is out-of-sync
  async checkIfHistoryOutOfSync() {
    const afterSaveLastHistory = await this.formHistory?.histories.last();
    const recentHistoryInView = document.querySelector('[data-history-item]');
    if (!recentHistoryInView) return;

    const datetime = recentHistoryInView.getAttribute('datetime');
    if (afterSaveLastHistory.time > Number(datetime)) {
      this.dispatch('history-out-of-sync', { bubbles: true });
    }
  }

  get isOnline() {
    if ('onLine' in navigator) {
      return !!navigator.onLine;
    }

    return true;
  }
}

class FormHistory {
  constructor(db, { form, historyLimit }) {
    this.db = db;
    this.form = form;
    this.storageKey = computeStorageKey(form);
    this.historyLimit = historyLimit;
    this.historyModal = document.getElementById('history-modal');

    // Update "Last Saved" time every 1 second.
    this.interval = setInterval(this.updateLastSavedAt.bind(this), 60 * 1000);

    const autoSaveMenu = document.getElementById('auto-save-menu');
    if (!autoSaveMenu) return;

    const slot = document.querySelector('[data-auto-save-slot]');
    if (slot) {
      slot.appendChild(autoSaveMenu);
      autoSaveMenu.classList.add('is-slotted');
      autoSaveMenu.style.display = 'inline-flex';

      // When the slot scrolls past the fixed page header (~70px), pin the
      // menu to the top-right of the viewport so the controls stay reachable.
      // When the slot scrolls back into view, the pin releases.
      this.pinObserver = new IntersectionObserver(
        ([entry]) => {
          autoSaveMenu.classList.toggle('is-floating', !entry.isIntersecting);
        },
        { rootMargin: '-70px 0px 0px 0px', threshold: 0 }
      );
      this.pinObserver.observe(slot);
    } else {
      autoSaveMenu.classList.remove('is-slotted');
      autoSaveMenu.style.display = 'block';
    }

    this.historyBtn = autoSaveMenu.querySelector('#auto-save-history-button');
    this.manualSaveBtn = autoSaveMenu.querySelector('#auto-save-manual-button');
    this.loadLastHistoryBtn = autoSaveMenu.querySelector('#auto-save-load-button');
    if (!this.historyBtn || !this.manualSaveBtn || !this.loadLastHistoryBtn) return;

    this.onHistoryBtnClick = this.onHistoryBtnClick.bind(this);
    this.onManualSaveBtnClick = this.onManualSaveBtnClick.bind(this);
    this.onLoadLastHistoryBtnClick = this.onLoadLastHistoryBtnClick.bind(this);
    this.appendHistoryItem = this.appendHistoryItem.bind(this);
    this.populateElement = this.populateElement.bind(this);

    this.historyBtn.addEventListener('click', this.onHistoryBtnClick);
    this.manualSaveBtn.addEventListener('click', this.onManualSaveBtnClick);
    this.loadLastHistoryBtn.addEventListener('click', this.onLoadLastHistoryBtnClick);

    // This should be invoked after initializing everything.
    this.updateLastSavedAt();
  }

  destroy() {
    this.historyBtn?.removeEventListener('click', this.onHistoryBtnClick);
    this.manualSaveBtn?.removeEventListener('click', this.onManualSaveBtnClick);
    this.loadLastHistoryBtn?.removeEventListener('click', this.onLoadLastHistoryBtnClick);
    this.pinObserver?.disconnect();
    if (this.interval) clearInterval(this.interval);
  }

  async load(id) {
    const history = (await this.histories.toArray()).find((h) => h.id.toString() === id.toString());
    if (!history) return;

    history.children.forEach(this.populateElement);
    $(this.historyModal).modal('hide');
    // Notify listeners (rich-text editors etc.) that the form was restored.
    // The history-modal path also dispatches this from the outer controller,
    // but the floating "Load last save" button skips that path.
    this.form.dispatchEvent(new CustomEvent('auto-save:loaded', { bubbles: true }));
  }

  async populateElement(object) {
    const { id, value, isSelectize, children, htmlDump } = object;

    if (htmlDump) {
      const container = document.getElementById(id);
      if (!container) return;

      container.innerHTML = htmlDump;
      await nextFrame();
      children.forEach((child) => findAndUpdateElement(child));
    } else {
      findAndUpdateElement({ id, value, isSelectize });
    }

    // Initialize add field buttons so that new nested fields can be appended
    // TODO: Implement `MutationObserver` on `AddFields` so that it can automatically detect for DOM changes
    await nextFrame();
    // eslint-disable-next-line no-new
    new AddFields();
  }

  async onHistoryBtnClick(event) {
    event.preventDefault();
    $(event.currentTarget).tooltip('hide');
    $(event.currentTarget).trigger('blur');

    const unsubmittedDataCount = await this.histories.count();
    if (!unsubmittedDataCount) {
      const historyBody = this.historyModal.querySelector('[data-histories]');
      historyBody.replaceChildren();

      const newRow = document.createElement('tr');
      const newCell = document.createElement('td');

      newCell.innerHTML = "<em>There isn't any history that you can restore for this form, yet.</em>";
      newCell.setAttribute('colspan', '2');

      newRow.appendChild(newCell);
      historyBody.appendChild(newRow);
    }

    if (!this.historyModal) return;
    if (unsubmittedDataCount) await this.populateHistories();
    this.populateFormTitle();
    // Only show modal after appending all the histories
    $(this.historyModal).modal('show');
  }

  // Display which form these saved drafts belong to. Custom-form pages opt
  // in by setting `data-auto-save-title` from `template.title`, in which
  // case we render "Template: <name>". Forms outside the custom-form system
  // fall back to the page `<title>`, stripping the " | Ambiki" suffix
  // added by `full_title`.
  populateFormTitle() {
    const target = this.historyModal.querySelector('[data-form-title]');
    if (!target) return;

    const templateTitle = (this.form.dataset.autoSaveTitle || '').trim();
    const fallback = document.title.replace(/\s*\|\s*Ambiki\s*$/, '').trim();
    const label = templateTitle ? `Template: ${templateTitle}` : fallback;

    if (label) {
      target.textContent = label;
      target.hidden = false;
    } else {
      target.textContent = '';
      target.hidden = true;
    }
  }

  async onManualSaveBtnClick(event) {
    event.preventDefault();
    $(event.currentTarget).tooltip('hide');
    $(event.currentTarget).trigger('blur');
    await this.save();
  }

  async onLoadLastHistoryBtnClick(event) {
    event.preventDefault();
    $(event.currentTarget).tooltip('hide');
    $(event.currentTarget).trigger('blur');
    const lastHistory = await this.histories.last();
    if (!lastHistory) {
      // Nothing saved yet — let the user know with a self-dismissing toast rather than an
      // uninitialized popover, which used to wedge open between the buttons until a refresh.
      $('#autosave-no-history-toast').toast('show');
      return;
    }
    await this.load(lastHistory.id);
  }

  async save(showToast = true) {
    await this.db.forms.add(this.constructDataHash({ children: traverseDOM(this.form) }));
    const totalCount = await this.histories.count();
    const diff = totalCount - this.historyLimit;
    if (diff > 0) {
      await this.histories.limit(diff).delete();
    }

    if (showToast) {
      $('#save-toast').toast('show');
    }

    // Update "Last Saved" time after saving the data
    await this.updateLastSavedAt();
  }

  async populateHistories() {
    if (!this.historyModal) return;

    const historyBody = this.historyModal.querySelector('[data-histories]');
    historyBody.replaceChildren();

    const histories = await this.histories.reverse().toArray();
    histories.forEach(this.appendHistoryItem);
  }

  appendHistoryItem(history) {
    const historyContainer = this.historyModal.querySelector('[data-histories-container]');
    const historyBody = this.historyModal.querySelector('[data-histories]');
    const template = historyContainer.querySelector('[data-history-item-template]');

    const clonedHistoryItem = template.content.cloneNode(true);
    const trContainer = clonedHistoryItem.querySelector('[data-history-item]');
    const datetimeContainer = clonedHistoryItem.querySelector('[data-datetime]');
    const loadHistoryBtn = clonedHistoryItem.querySelector('[data-load-history-btn]');

    trContainer.setAttribute('datetime', history.time.toString());
    datetimeContainer.textContent = new Date(history.time).toLocaleString('en-US');
    loadHistoryBtn.setAttribute('data-id', history.id);

    historyBody.append(clonedHistoryItem);
  }

  constructDataHash(hash = {}) {
    // Using a string `false` to avoid: https://stackoverflow.com/questions/48149851/failed-to-execute-only-on-idbkeyrange-the-parameter-is-not-a-valid-key
    // "This error is generally thrown when you are passing - boolean, null or undefined value."
    return { form_id: this.storageKey, time: Date.now(), submitted: 'false', ...hash };
  }

  async pruneSubmitted() {
    await this.db.forms.where({ form_id: this.storageKey, submitted: 'true' }).delete();
  }

  async updateLastSavedAt() {
    const element = document.getElementById('auto-save-time-ago');
    if (!element) return;

    const recentHistory = await this.histories.last();
    if (recentHistory) {
      element.textContent = `Last Saved: ${dayjs(recentHistory.time).fromNow()}`;
      element.hidden = false;
    } else {
      element.hidden = true;
    }
  }

  /**
   * @returns {Promise<any>}
   */
  get histories() {
    return this.db.forms.where({ form_id: this.storageKey, submitted: 'false' });
  }
}

// New-record forms (`form_for @new_record`) get a generic Rails-generated id
// like `new_care_plan` or `new_evaluation` regardless of which patient/org
// the form belongs to. Without scoping, every "new" form across patients
// shares one IndexedDB bucket. Edit forms already include the record UUID
// so they're scoped correctly — leave those alone to preserve drafts.
//
// Forms opt in to a semantic scope via `data-auto-save-scope` — e.g.
// "Evaluation;patient:abc;template:123". That stays stable across the
// session-timeout sign-in round-trip and discipline switches (which
// also swap templates and so still produce a different bucket — the
// template id is what identifies the form structure). Forms that don't
// opt in fall back to pathname + search (legacy behavior).
function computeStorageKey(form) {
  if (form.id.startsWith('new_')) {
    const scope = form.dataset.autoSaveScope;
    if (scope) return `${form.id}::${scope}`;
    return `${form.id}::${window.location.pathname}${window.location.search}`;
  }
  return form.id;
}

/**
 * @description Walk the DOM recursively and create an object with the following data structure for the given DOM:
 * @example
 * <div>
 *   <input id="1" type="text" name="care_plan_title" />
 *   <div id="some_id" data-fields="some_nested_field">
 *     <textarea id="2" name="care_plan[long_term_goals][235245][goal]"></textarea>
 *     <div data-fields="short_term_goal">
 *       <textarea id="3" name="care_plan[long_term_goals][235245][short_term_goals][1235989][goal]"></textarea>
 *     </div>
 *   </div>
 * </div>
 *
 * [
 *   {
 *     id: "1",
 *     nodeName: "INPUT",
 *     name: "care_plan_title",
 *     value: "",
 *     isSelectize: false
 *   },
 *   {
 *     htmlDump: "<textarea name="care_plan[long_term_goals][235245][goal]">...</textarea><div>...</div>",
 *     id: "some_id",
 *     children: [
 *       {
 *         id: "2",
 *         nodeName: "TEXTAREA",
 *         name: "care_plan[long_term_goals][235245][goal]",
 *         value: "",
 *         isSelectize: false,
 *       },
 *       {
 *         // more objects
 *       }
 *     ]
 *   }
 * ]
 * @param {Node} node
 * @param {Node[]} children
 * @param {boolean} makeDump
 */
function traverseDOM(node, children = [], makeDump = true) {
  // Note: This is not the same as `const childrenDup = [...children]` as that would be cloning the elements. When
  // traversing, we need the exact reference of the Node to successfully transfer any kind of `addEventListener` that
  // has been already attached
  const childrenDup = children;

  for (let i = 0; i < node.childNodes.length; i += 1) {
    const currentNode = node.childNodes[i];

    if (isTopLevelNestedContainer(currentNode) && makeDump) {
      childrenDup.push({
        children: traverseDOM(currentNode, [], false),
        htmlDump: cleanHTML(currentNode),
        id: currentNode.id,
        nodeName: currentNode.nodeName,
      });
    } else if (isHTMLFieldLike(currentNode) && !!currentNode.id && !currentNode.id.includes('selectized')) {
      childrenDup.push({
        id: currentNode.id,
        isSelectize: isSelectizeInstance(currentNode),
        nodeName: currentNode.nodeName,
        name: currentNode.name,
        type: getElementType(currentNode),
        value: isCheckable(currentNode) ? currentNode.checked : currentNode.value,
      });
    } else {
      traverseDOM(currentNode, childrenDup);
    }
  }

  return childrenDup;
}

function isHTMLFieldLike(node) {
  return (
    (node instanceof HTMLInputElement && node.type !== 'hidden' && node.type !== 'submit') ||
    node instanceof HTMLTextAreaElement ||
    node instanceof HTMLSelectElement ||
    node.nodeName === 'TRIX-EDITOR'
  );
}

function findAndUpdateElement({ id, value, isSelectize }) {
  if (!id) return;
  // Looks like selectize adds a hidden input field with the same `id`.
  const htmlField = document.querySelector(`#${CSS.escape(id)}:not([type="hidden"])`);
  if (!htmlField) return;

  if (htmlField.classList.contains('hidden')) {
    const container = document.querySelector(`div[data-id="${CSS.escape(id)}"]`);
    if (!container) return;

    const autoSaveLoad = new CustomEvent('auto-save-load', {
      detail: {
        data: value,
      },
    });
    container.dispatchEvent(autoSaveLoad);
  }

  if (isSelectize) {
    if (isSelectizeInstance(htmlField)) {
      htmlField.selectize.addItem(value);
    } else {
      // In an ideal world, these attributes should've been initialized within the select field as HTML attributes
      $(htmlField).selectize({
        create: false,
        sortField: 'id',
        placeholder: htmlField.getAttribute('placeholder'),
        onDropdownClose(dropdown) {
          $(dropdown).find('.selected').not('.active').removeClass('selected');
        },
      });
    }

    return;
  }

  if (isCheckable(htmlField)) {
    htmlField.checked = value;
    return;
  }

  htmlField.value = value;
}

function isCheckable(node) {
  return ['checkbox', 'radio'].includes(node.type);
}

function isSelectizeInstance(node) {
  return typeof node.selectize === 'object';
}

function isTopLevelNestedContainer(node) {
  return node instanceof HTMLElement && node.hasAttribute('data-nested') && !node.parentNode.closest('[data-nested]');
}

function cleanHTML(node) {
  const parser = new DOMParser();
  const parsedHTML = parser.parseFromString(node.innerHTML, 'text/html');

  // Remove `data-add-links-listener` attribute before making a dump so that we can initialize later.
  // See: app/javascript/helpers/nested-fields/addFields.js#L25
  const attribute = 'data-add-links-listener';
  Array.from(parsedHTML.querySelectorAll(`[${attribute}]`)).forEach((s) => s.removeAttribute(attribute));

  // Add selectize options back to select fields
  const selects = Array.from(node.querySelectorAll('select')).filter((s) => isSelectizeInstance(s));
  selects.forEach((select) => {
    const inputId = select.selectize.$input[0].id;
    const selectField = parsedHTML.getElementById(inputId);
    if (!selectField) return;

    copySelectizeAttributes(select, selectField);
  });

  // Remove instances of `selectize` before making a dump. If we pair up `selectize` and `stimulus`, this won't be necessary
  Array.from(parsedHTML.querySelectorAll('.selectize-control')).forEach((s) => s.remove());
  return parsedHTML.body.innerHTML;
}

function copySelectizeAttributes(fromSelect, toSelect) {
  toSelect.required = fromSelect.selectize.isRequired;
  toSelect.setAttribute('placeholder', fromSelect.selectize.$control_input[0].placeholder);

  const optionValues = Array.from(toSelect.options).map(({ value }) => value);
  Object.entries(fromSelect.selectize.options).forEach(([key, object]) => {
    if (optionValues.includes(key)) return;

    const option = document.createElement('option');
    option.value = key;
    option.innerText = object.text;
    option.disabled = object.disabled;
    option.selected = fromSelect.selectize.items.includes(object.value);
    toSelect.append(option);
  });
}

function getElementType(node) {
  if (node.type) {
    return node.type;
  }

  if (typeof customElements.get(node.tagName.toLowerCase()) === 'function') {
    return 'custom-element';
  }

  return 'unknown';
}
